import React, { useState, useEffect, useRef, useCallback } from "react";
import { DayWithHours } from "../../../../types";
import {
  Box,
  Button,
  Typography,
  Badge,
  Tooltip,
  useTheme,
  CircularProgress,
} from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import { postCalendarWeekChosen } from "../../../../axios/chat";
import { toast } from "react-toastify";

import {
  containerStyle,
  daysContainerStyle,
  badgeStyle,
  dayButtonStyle,
  dayCardStyle,
  dayTitleStyle,
  hoursOuterContainerStyle,
  hoursInnerContainerStyle,
  hourRowStyle,
  hourBoxStyle,
  hourTextStyle,
  timeSectionHeaderStyle,
  sendButtonContainerStyle,
  sendButtonStyle,
  emptySelectionStyle,
  clearButtonStyle,
} from "./styles";

const TIME_SECTIONS = [
  { label: "Early", start: 1, end: 6 },
  { label: "Morning", start: 6, end: 12 },
  { label: "Afternoon", start: 12, end: 18 },
  { label: "Evening", start: 18, end: 25 },
] as const;

const formatHour = (hour: number): string => {
  if (hour === 12) return "12:00 PM";
  if (hour === 24) return "12:00 AM";
  if (hour < 12) return `${hour}:00 AM`;
  return `${hour - 12}:00 PM`;
};

const formatHourCompact = (hour: number): string => {
  if (hour === 12) return "12PM";
  if (hour === 24) return "12AM";
  if (hour < 12) return `${hour}AM`;
  return `${hour - 12}PM`;
};

interface CalendarWeekProps {
  days: DayWithHours[];
  onSelectHour?: (day: string, hour: number) => void;
  availableDays?: number;
  availableHoursPerDay?: number;
  messageId: string;
}

const CalendarWeek = ({
  days,
  onSelectHour,
  availableDays,
  availableHoursPerDay,
  messageId,
}: CalendarWeekProps) => {
  const theme = useTheme();
  const [selectedHours, setSelectedHours] = useState<Record<string, number[]>>(
    {}
  );
  const [selectedDay, setSelectedDay] = useState("");
  const [isSending, setIsSending] = useState(false);
  const hoursContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (days.length > 0) {
      setSelectedDay(days[0].day);
    }
    const initialHours: Record<string, number[]> = {};
    days.forEach((day) => {
      initialHours[day.day] = day.hours ?? [];
    });
    setSelectedHours(initialHours);
  }, [days]);

  const totalSelectedCount = Object.values(selectedHours).reduce(
    (sum, hours) => sum + hours.length,
    0
  );

  const handleHourClick = useCallback(
    (day: string, hour: number) => {
      setSelectedHours((prev) => {
        const currentHours = prev[day] ?? [];
        const isSelected = currentHours.includes(hour);

        if (isSelected) {
          const updatedHours = currentHours.filter((h) => h !== hour);
          onSelectHour?.(day, hour);
          return { ...prev, [day]: updatedHours };
        }

        if (currentHours.length === 0 && availableDays !== undefined) {
          const daysWithSelection = Object.values(prev).filter(
            (hours) => hours.length > 0
          ).length;
          if (daysWithSelection >= availableDays) {
            toast.error("Maximum lessons per week reached");
            return prev;
          }
        }

        if (availableHoursPerDay !== undefined) {
          if (availableHoursPerDay === 1 && currentHours.length === 1) {
            const updatedHours = [hour];
            onSelectHour?.(day, hour);
            return { ...prev, [day]: updatedHours };
          }
          if (currentHours.length >= availableHoursPerDay) {
            toast.error(`Maximum ${availableHoursPerDay} slots per day`);
            return prev;
          }
        }

        const updatedHours = [...currentHours, hour].sort((a, b) => a - b);
        onSelectHour?.(day, hour);
        return { ...prev, [day]: updatedHours };
      });
    },
    [onSelectHour, availableDays, availableHoursPerDay]
  );

  const handleSendCalendar = useCallback(async () => {
    const calendarData = {
      message_id: messageId,
      days: days.map((dayObj) => ({
        day: dayObj.day,
        hours: selectedHours[dayObj.day] ?? [],
      })),
    };

    try {
      setIsSending(true);
      await postCalendarWeekChosen(calendarData);
      toast.success("Schedule sent successfully");
    } catch (error) {
      console.error("Error sending calendar data:", error);
      toast.error("Failed to send schedule. Please try again.");
    } finally {
      setIsSending(false);
    }
  }, [messageId, days, selectedHours]);

  const handleClearAll = useCallback(() => {
    setSelectedHours((prev) => {
      const cleared: Record<string, number[]> = {};
      days.forEach((day) => {
        cleared[day.day] = [];
      });
      return cleared;
    });
  }, [days]);

  useEffect(() => {
    if (selectedDay && selectedHours[selectedDay]?.length) {
      const firstSelectedHour = selectedHours[selectedDay][0];
      const el = document.getElementById(
        `hour-${selectedDay}-${firstSelectedHour}`
      );
      el?.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [selectedDay, selectedHours]);

  const getHoursForSection = (start: number, end: number) =>
    Array.from({ length: end - start }, (_, i) => start + i);

  return (
    <Box sx={containerStyle} role="region" aria-label="Weekly schedule picker">
      <Box sx={daysContainerStyle} role="group" aria-label="Day selection">
        {days.map((dayObj) => {
          const dayHours = selectedHours[dayObj.day] ?? [];
          const selectedCount = dayHours.length;
          const isSelected = selectedDay === dayObj.day;

          const badgeLabel =
            selectedCount === 0
              ? ""
              : selectedCount === 1
                ? formatHourCompact(dayHours[0])
                : selectedCount === 2
                  ? `${formatHourCompact(dayHours[0])}, ${formatHourCompact(dayHours[1])}`
                  : `${formatHourCompact(dayHours[0])}, ${formatHourCompact(dayHours[1])} +${selectedCount - 2}`;

          return (
            <Tooltip
              key={dayObj.day}
              title={
                selectedCount > 0
                  ? dayHours.map(formatHour).join(", ")
                  : "Select time slots"
              }
              placement="top"
            >
              <Badge
                badgeContent={badgeLabel}
                invisible={selectedCount === 0}
                sx={badgeStyle}
              >
                <Button
                  variant={isSelected ? "contained" : "outlined"}
                  onClick={() => setSelectedDay(dayObj.day)}
                  sx={dayButtonStyle(isSelected, theme)}
                  aria-pressed={isSelected}
                  aria-label={
                    selectedCount > 0
                      ? `${dayObj.day}, ${dayHours.map(formatHour).join(", ")}`
                      : `${dayObj.day}`
                  }
                >
                  {dayObj.day}
                </Button>
              </Badge>
            </Tooltip>
          );
        })}
      </Box>

      <Box sx={dayCardStyle}>
        {selectedDay && (
          <Box>
            <Typography sx={dayTitleStyle} component="h3">
              {selectedDay}
            </Typography>
            <Box sx={hoursOuterContainerStyle}>
              <Box ref={hoursContainerRef} sx={hoursInnerContainerStyle}>
                {TIME_SECTIONS.map((section) => {
                  const sectionHours = getHoursForSection(
                    section.start,
                    section.end
                  );

                  return (
                    <Box key={section.label} role="group">
                      <Typography
                        component="span"
                        sx={timeSectionHeaderStyle}
                        aria-hidden
                      >
                        {section.label}
                      </Typography>
                      {sectionHours.map((hour) => {
                        const isHourSelected =
                          selectedHours[selectedDay]?.includes(hour);
                        const selectedForDay =
                          selectedHours[selectedDay] ?? [];
                        const firstSelected = selectedForDay[0] ?? null;

                        return (
                          <Box
                            key={hour}
                            id={
                              firstSelected === hour
                                ? `hour-${selectedDay}-${hour}`
                                : undefined
                            }
                            sx={hourRowStyle}
                            role="button"
                            tabIndex={0}
                            onClick={() => handleHourClick(selectedDay, hour)}
                            onKeyDown={(e) => {
                              if (
                                e.key === "Enter" ||
                                e.key === " "
                              ) {
                                e.preventDefault();
                                handleHourClick(selectedDay, hour);
                              }
                            }}
                            aria-pressed={isHourSelected}
                            aria-label={`${formatHour(hour)}, ${isHourSelected ? "selected" : "not selected"}`}
                          >
                            <Box sx={hourBoxStyle(isHourSelected)}>
                              {isHourSelected && (
                                <CheckIcon sx={{ fontSize: 18, color: "#fff" }} />
                              )}
                            </Box>
                            <Typography variant="body2" sx={hourTextStyle}>
                              {formatHour(hour)}
                            </Typography>
                          </Box>
                        );
                      })}
                    </Box>
                  );
                })}
              </Box>
            </Box>

            {totalSelectedCount > 0 && (
              <Button
                size="small"
                onClick={handleClearAll}
                sx={clearButtonStyle}
                aria-label="Clear all selections"
              >
                Clear all
              </Button>
            )}
          </Box>
        )}

        {totalSelectedCount === 0 && (
          <Typography sx={emptySelectionStyle} variant="body2">
            Select at least one time slot
          </Typography>
        )}

        <Box sx={sendButtonContainerStyle}>
          <Button
            variant="outlined"
            onClick={handleSendCalendar}
            disabled={isSending || totalSelectedCount === 0}
            sx={sendButtonStyle}
            startIcon={
              isSending ? (
                <CircularProgress size={18} color="inherit" />
              ) : undefined
            }
            aria-busy={isSending}
          >
            {isSending
              ? "Sending…"
              : totalSelectedCount > 0
                ? `Send schedule (${totalSelectedCount})`
                : "Send schedule"}
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default CalendarWeek;
